import { Box, Stack } from "@mui/material";
import parlors from "goi_common/src/constants/parlors_detail.json";

import {
  ParlorRegionHeadSection,
  BTypeCardView,
  BTypeRegionHead,
  DTypeParlorList,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  ParlorDetailProps,
  numberToLocalString,
  colors,
  CTypeBreadCrumb,
  BTypeCTA,
  GOI_WEB_URL,
  buildParlorRegionPageSchema,
  ParlorRegionFAQ,
  RichSearch,
} from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";

// Path 로직 안내
// FUNERAL_SIDO, SIGUNGU의 key로 path 설정.
//
// 1. getStaticPaths 내 key,value 받아서 value(key)로 추가한 뒤 params로 넘기게 변경
// 2. getStaticProps 내 props sido,sigungu, sidoName, sigunguName에 split,replace 로직 추가

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const sidoList = Array.from(FUNERAL_SIDO).filter(([key, val]) =>
    targetSidos.includes(String(key))
  );

  const sidoPath = sidoList.map(([key, val]) => {
    return {
      params: {
        code: `${val}도 의 장례식장들`,
      },
    };
  });

  const sigunguPaths = Array.from(FUNERAL_SIDO)
    .filter(([key, val]) => targetSidos.includes(String(key)))
    .map(([sidoKey, sidoVal]) => {
      const sigungus = FUNERAL_SIGUNGU.get(sidoKey);
      if (sigungus) {
        return Array.from(sigungus).map(([sigunguKey, sigunguVal]) => {
          return {
            params: {
              code: `${sidoVal}도-${sigunguVal} 의 장례식장들`,
            },
          };
        });
      }
    })
    .filter((i) => i != null);

  return {
    paths: sidoPath.concat(sigunguPaths?.flat() as Array<any>),
    fallback: false,
  };
};

function getByValue(map: Map<number, any>, searchValue: any) {
  for (const [key, value] of map.entries()) {
    if (value === searchValue) return key;
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.code as string;

  if (code.includes("-")) {
    const [sido, sigungu] = code.split("-");
    const sidoName = sido.replace("도", "");
    const sidoNumber = getByValue(FUNERAL_SIDO, sidoName);
    const sigunguName = sigungu.replace(" 의 장례식장들", "");
    const sigunguNumber = getByValue(
      FUNERAL_SIGUNGU.get(sidoNumber as number) as any,
      sigunguName
    );

    const regionParlors = parlors.filter(
      (p) => String(p.sigungu) === String(sigunguNumber)
    );

    const 가족장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["가족장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 무빈소장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["무빈소"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 일반장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["일반장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();

    let parlorsString = "";
    regionParlors.map((p: any) => {
      if (p.binsos.length > 0) {
        parlorsString += `${p.companyname} ${numberToLocalString(
          p.binsos[0].charge
        )}원, `;
      }
    });

    const description = `${sidoName} ${sigunguName} 내 ${regionParlors.length}곳의 장례식장을 대상으로 한 비용 비교: 강원 내 모든 장례식장에 대한 비용을 알아보세요. 근조화환, 실시간 빈소, 장례식장 위치 등 해당 장례식장에 필요한 정보들을 알아보세요. (고이장례연구소)`;

    return {
      props: {
        regionParlors,
        expectedParlorPrice: { 무빈소장, 가족장, 일반장 },
        sidoName,
        sido: sidoNumber,
        sigunguName,
        sigungu: sigunguNumber,
        isSigungu: true,
        pageDescription: description,
        pageKeywords: `${sidoName} ${sigunguName} 장례식장, ${sidoName} ${sigunguName} 장례식장 추천, ${sidoName} ${sigunguName} 장례식장 비용, ${sidoName} ${sigunguName} 장례식장 평균 예상 비용, ${sidoName} ${sigunguName}  무빈소, ${sidoName} ${sigunguName}  가족장, ${sidoName} ${sigunguName} `,
        pageOgTitle: `${sidoName} ${sigunguName} 지역의 장례식장 목록, 추천, 안내 :: 강원장례정보공개 (고이장례연구소)`,
        pageOgDescription: description,
        pageOgImage:
          regionParlors.length > 0
            ? regionParlors[0]?.images[0]?.url || ""
            : "",
      },
    };
  } else {
    const sidoName = code.replace("도 의 장례식장들", "");
    const sidoNumber = getByValue(FUNERAL_SIDO, sidoName);
    const regionParlors = parlors.filter((p) => p.sido === String(sidoNumber));

    const 가족장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["가족장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 무빈소장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["무빈소"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 일반장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["일반장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();

    let parlorsString = "";
    regionParlors.map((p: any) => {
      parlorsString += `${p.companyname} `;
    });

    const description = `강원장례정보공개 [고이장례연구소] - ${sidoName} 내 ${regionParlors.length}곳의  장례식장 정보, 비용 비교: 빈소 비용 및 추가 비용에 대한 정보. 주변 지역 장례식장 함께 알아보기 ${parlorsString}`;

    return {
      props: {
        regionParlors,
        expectedParlorPrice: { 무빈소장, 가족장, 일반장 },
        sidoName,
        sido: sidoNumber,
        isSigungu: false,
        pageDescription: description,
        pageKeywords: `${sidoName} 장례식장, ${sidoName} 장례식장 추천, ${sidoName} 장례식장 비용, ${sidoName} 장례식장 평균 예상 비용, ${sidoName} 무빈소, ${sidoName} 가족장, ${sidoName} `,
        pageOgTitle: `${sidoName} 지역 장례식장 추천, 비교, 안내 :: 강원장례정보공개 (고이장례연구소)`,
        pageOgDescription: description,
        pageOgImage:
          regionParlors.length > 0
            ? regionParlors[0]?.images[0]?.url || ""
            : "",
      },
    };
  }
};

export default function ParlorSlugTypePage({
  regionParlors,
  expectedParlorPrice,
  pageOgTitle,
  sido,
  sidoName,
  sigungu,
  sigunguName,
  isSigungu,
}: {
  regionParlors: Array<ParlorDetailProps>;
  expectedParlorPrice: { 무빈소장: number; 가족장: number; 일반장: number };
  pageOgTitle: string;
  sido: string;
  sidoName: string;
  sigungu?: string;
  sigunguName?: string;
  isSigungu: boolean;
}) {
  if (regionParlors.length === 0) {
    return <></>;
  }

  const parlorRegionPageSchema = buildParlorRegionPageSchema(regionParlors, {
    regionText: `${sidoName}${isSigungu && sigunguName ? ` ${sigunguName}` : ""}`,
    itemUrlBuilder: (id) =>
      `https://www.gangwon-portal.co.kr/gangwon-parlor-places/${id}/`,
  });

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
      </Head>
      <RichSearch data={parlorRegionPageSchema} />

      <ParlorRegionHeadSection
        breadCrumb={
          <CTypeBreadCrumb
            firstItem={{
              text: `장례식장 추천`,
              href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
              event: {
                domain: "gangwon",
                path: "prlrs",
                trigger_and_target: "br_crumb_mth_req",
              },
            }}
            secondItem={{
              text: `${sidoName} 장례식장`,
              href: `/gangwon-parlors/${sidoName}도 의 장례식장들/`,
              event: {
                domain: "gangwon",
                path: "prlrs",
                trigger_and_target: "br_crumb_sido_prlr_list",
              },
            }}
            thirdItem={
              isSigungu
                ? {
                    text: `${sigunguName} 장례식장`,
                    href: `/gangwon-parlors/${sidoName}도-${sigunguName} 의 장례식장들/`,
                    event: {
                      domain: "gangwon",
                      path: "prlrs",
                      trigger_and_target: "br_crumb_sigungu_prlr_list",
                    },
                  }
                : undefined
            }
            goiHomeEvent={{
              domain: "gangwon",
              path: "prlrs",
              trigger_and_target: "br_crumb_goi_home",
            }}
          />
        }
        title={
          <BTypeRegionHead
            regionParlors={regionParlors}
            sidoName={sidoName}
            sigunguName={sigunguName}
            expectedParlorPrice={expectedParlorPrice}
            mainColor={regionColor}
            event={{
              domain: "gangwon",
              path: "prlrs",
              trigger_and_target: "uv_mth_req",
            }}
          />
        }
        ctaList={[]}
      />

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <BTypeCTA
          sidoName={sidoName}
          sigunguName={sigunguName}
          mainColor={regionColor}
          event={{
            domain: "gangwon",
            path: "prlrs",
            trigger_and_target: "CTA_uv_mth_req",
          }}
        />
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <DTypeParlorList
          sido={sido}
          sigungu={sigungu}
          title={`${sidoName}${
            sigunguName ? " " + sigunguName : ""
          }의 다른 장례식장`}
          binsoAveragePrice={true}
          CardViewType={(res) => (
            <BTypeCardView
              descriptions={[
                `빈소 이용료: ${res.binsos[0].charge.toLocaleString()}원부터`,
              ]}
              mainColor={regionColor}
              key={res.companyname}
              name={res.companyname}
              href={`/gangwon-parlor-places/${res.id}/`}
              imgSrc={res.images[0] ? res.images[0].url : ""}
            />
          )}
        />
        <Box padding="20px 0 " />
        {isSigungu && (
          <DTypeParlorList
            sido={sido}
            title={`${sidoName}의 다른 장례식장`}
            binsoAveragePrice={true}
            CardViewType={(res) => (
              <BTypeCardView
                descriptions={[
                  `빈소 이용료: ${res.binsos[0].charge.toLocaleString()}원부터`,
                ]}
                mainColor={regionColor}
                key={res.companyname}
                name={res.companyname}
                href={`/gangwon-parlor-places/${res.id}/`}
                imgSrc={res.images[0] ? res.images[0].url : ""}
              />
            )}
          />
        )}
      </Stack>

      <ParlorRegionFAQ />
    </>
  );
}
